import React, { useState } from "react";
import { Box } from "@mui/material";
import Button from "components/basic/button";
import ConfirmDialog from "components/basic/confirmDialog";
import { VendorVerification } from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import ReasonDialog from "../../../../../ReasonDialog";

import { useAdminActions } from "../../../../../../adminApi";
import { AdminActionResult, AdminVendor } from "../../../../../../types";

interface VendorAdminActionsProps {
  vendor: AdminVendor;
  onChanged: () => void;
}

type ReasonAction = "reject" | "pause";

const VendorAdminActions: React.FC<VendorAdminActionsProps> = ({
  vendor,
  onChanged,
}) => {
  const showSnack = useSnack();
  const {
    approveVendor,
    rejectVendor,
    approveReverification,
    setVendorPaused,
    setVendorSuspended,
  } = useAdminActions();
  const [busy, setBusy] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const [reasonAction, setReasonAction] = useState<ReasonAction | null>(null);
  const suspended = Boolean(vendor.suspendedAt);
  const paused = Boolean(vendor.vendor.pausedAt);
  const status = vendor.vendor.verificationStatus;
  const name = vendor.vendor.businessName;

  const run = async (action: Promise<AdminActionResult>, done: string) => {
    setBusy(true);
    const result = await action;
    setBusy(false);
    if (!result.ok) {
      showSnack(result.error ?? "Something went wrong", "error");
      return false;
    }
    showSnack(done, "success");
    onChanged();
    return true;
  };

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
    >
      <Box display="flex" flexWrap="wrap" gap={1}>
        {status !== VendorVerification.Approved && (
          <Button
            size="small"
            color="success"
            disabled={busy}
            onClick={() =>
              run(approveVendor(vendor.id), "Vendor approved and emailed")
            }
          >
            Approve
          </Button>
        )}
        {status !== VendorVerification.Rejected && (
          <Button
            size="small"
            color="error"
            variant="outlined"
            disabled={busy}
            onClick={() => setReasonAction("reject")}
          >
            Reject
          </Button>
        )}
        {vendor.vendor.reverificationRequestedAt && (
          <Button
            size="small"
            color="success"
            disabled={busy}
            onClick={() =>
              run(approveReverification(vendor.id), "Changes approved")
            }
          >
            Approve changes
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={() =>
            paused
              ? run(setVendorPaused(vendor.id, false), "Vendor resumed")
              : setReasonAction("pause")
          }
        >
          {paused ? "Resume services" : "Pause services"}
        </Button>
      </Box>
      <Button
        size="small"
        color={suspended ? "primary" : "error"}
        loading={busy && confirmSuspend}
        disabled={busy}
        onClick={() =>
          suspended
            ? run(setVendorSuspended(vendor.id, false), "Vendor restored")
            : setConfirmSuspend(true)
        }
      >
        {suspended ? "Restore vendor" : "Suspend vendor"}
      </Button>

      {reasonAction === "reject" && (
        <ReasonDialog
          title={`Reject ${name}?`}
          body="The vendor is emailed this reason and can update and resubmit their application."
          confirmLabel="Reject"
          loading={busy}
          onConfirm={async (reason) => {
            if (
              await run(
                rejectVendor(vendor.id, reason),
                "Vendor rejected and emailed",
              )
            ) {
              setReasonAction(null);
            }
          }}
          onClose={() => setReasonAction(null)}
        />
      )}

      {reasonAction === "pause" && (
        <ReasonDialog
          title={`Pause ${name}?`}
          body="Their services are hidden, their portal turns off and new quotes are blocked, but they can still sign in to fix the issue. They are emailed this reason."
          confirmLabel="Pause"
          loading={busy}
          onConfirm={async (reason) => {
            if (
              await run(
                setVendorPaused(vendor.id, true, reason),
                "Vendor paused and emailed",
              )
            ) {
              setReasonAction(null);
            }
          }}
          onClose={() => setReasonAction(null)}
        />
      )}

      {confirmSuspend && (
        <ConfirmDialog
          title={`Suspend ${name}?`}
          body={`Their services and portal disappear from the marketplace, and all ${vendor.accountCount} team account(s) are signed out and can't sign back in until you restore them.`}
          confirmLabel="Suspend"
          destructive
          loading={busy}
          onConfirm={async () => {
            await run(setVendorSuspended(vendor.id, true), "Vendor suspended");
            setConfirmSuspend(false);
          }}
          onClose={() => setConfirmSuspend(false)}
        />
      )}
    </Box>
  );
};

export default VendorAdminActions;
